const tokens = {
  "#": { pattern: /[0-9]/ },
  "A": { pattern: /[a-zA-Z]/ },
  "*": { pattern: /[a-zA-Z0-9]/ }
};
class Mask {
  constructor(opts) {
    this.opts = opts;
  }
  memo = /* @__PURE__ */ new Map();
  masked(value) {
    return this.process(value, this.opts.format);
  }
  unmasked(value) {
    return this.process(value, this.opts.format, false);
  }
  isEager() {
    return !this.opts.lazy;
  }
  isReversed() {
    return this.opts.reversed === true;
  }
  completed(value) {
    const length = this.process(value, this.opts.format).length;
    return length >= this.opts.format.length;
  }
  escapeMask(maskRaw) {
    const chars = [];
    const escaped = [];
    maskRaw.split("").forEach((ch, i) => {
      if (ch === "!" && maskRaw[i - 1] !== "!") {
        escaped.push(i - escaped.length);
      } else {
        chars.push(ch);
      }
    });
    return { mask: chars.join(""), escaped };
  }
  process(value, maskRaw, masked = true) {
    if (maskRaw == null)
      return value;
    const key = `value=${value},mask=${maskRaw},masked=${masked ? 1 : 0}`;
    if (this.memo.has(key))
      return this.memo.get(key);
    const { mask, escaped } = this.escapeMask(maskRaw);
    const result = [];
    const offset = this.isReversed() ? -1 : 1;
    const method = this.isReversed() ? "unshift" : "push";
    const lastMaskChar = this.isReversed() ? 0 : mask.length - 1;
    const check = this.isReversed() ? () => m > -1 && v > -1 : () => m < mask.length && v < value.length;
    const notLastMaskChar = (m2) => !this.isReversed() && m2 <= lastMaskChar || this.isReversed() && m2 >= lastMaskChar;
    let lastRawMaskChar;
    let repeatedPos = -1;
    let m = this.isReversed() ? mask.length - 1 : 0;
    let v = this.isReversed() ? value.length - 1 : 0;
    while (check()) {
      const maskChar = mask.charAt(m);
      const token = tokens[maskChar];
      const valueChar = token?.transform != null ? token.transform(value.charAt(v)) : value.charAt(v);
      if (!escaped.includes(m) && token != null) {
        if (valueChar.match(token.pattern) != null) {
          result[method](valueChar);
          if (token.repeated) {
            if (repeatedPos === -1) {
              repeatedPos = m;
            } else if (m === lastMaskChar && m !== repeatedPos) {
              m = repeatedPos - offset;
            }
            if (lastMaskChar === repeatedPos) {
              m -= offset;
            }
          } else if (token.multiple) {
            m -= offset;
          }
          m += offset;
        } else if (token.multiple) {
          const hasValue = result[v - offset]?.match(token.pattern) != null;
          const nextMask = mask.charAt(m + offset);
          if (hasValue && nextMask !== "" && tokens[nextMask] == null) {
            m += offset;
            v -= offset;
          } else {
            result[method]("");
          }
        } else if (valueChar === lastRawMaskChar) {
          lastRawMaskChar = void 0;
        } else if (token.optional) {
          m += offset;
          v -= offset;
        } else {
        }
        v += offset;
      } else {
        if (masked && !this.isEager()) {
          result[method](maskChar);
        }
        if (valueChar === maskChar && !this.isEager()) {
          v += offset;
        } else {
          lastRawMaskChar = maskChar;
        }
        if (!this.isEager()) {
          m += offset;
        }
      }
      if (this.isEager()) {
        while (notLastMaskChar(m) && (tokens[mask.charAt(m)] == null || escaped.includes(m))) {
          if (masked) {
            result[method](mask.charAt(m));
          } else if (mask.charAt(m) === value.charAt(v)) {
            v += offset;
          }
          m += offset;
        }
      }
    }
    this.memo.set(key, result.join(""));
    return this.memo.get(key);
  }
}
class MaskInput {
  constructor(input, options) {
    this.input = input;
    this.options = options;
    this.mask = new Mask(options);
    if (this.input.value !== "") {
      this.setMaskedValue(this.input.value);
    }
    this.input.addEventListener("input", this.handleInputEvent);
    this.input.addEventListener("beforeinput", this.handleBeforeInputEvent);
  }
  mask;
  needUpdateOptions(options) {
    return JSON.stringify(this.options) !== JSON.stringify(options);
  }
  needUpdateValue() {
    const value = this.input.dataset.maskValue;
    return value == null && this.input.value !== "" || value != null && value !== this.input.value;
  }
  destroy() {
    this.input.removeEventListener("input", this.handleInputEvent);
    this.input.removeEventListener("beforeinput", this.handleBeforeInputEvent);
  }
  handleBeforeInputEvent = (e) => {
    const input = e.target;
    if (this.mask.isEager() && "inputType" in e && e.inputType.startsWith("delete") && this.mask.unmasked(input.value).length <= 1) {
      this.setMaskedValue("");
    }
  };
  handleInputEvent = (e) => {
    if (e instanceof CustomEvent && e.type === "input" && e.detail != null && typeof e.detail === "object" && "masked" in e.detail) {
      return;
    }
    const input = e.target;
    const valueOld = input.value;
    const ss = input.selectionStart;
    const se = input.selectionEnd;
    let value = valueOld;
    if (this.mask.isEager()) {
      const masked = this.mask.masked(valueOld);
      const unmasked = this.mask.unmasked(valueOld);
      if (unmasked === "" && "data" in e && e.data != null) {
        value = e.data;
      } else if (unmasked !== this.mask.unmasked(masked)) {
        value = unmasked;
      }
    }
    this.setMaskedValue(value);
    if ("inputType" in e) {
      if (e.inputType.startsWith("delete") || ss != null && ss < valueOld.length) {
        try {
          input.setSelectionRange(ss, se);
        } catch {
        }
      }
    }
  };
  setMaskedValue(value) {
    const masked = this.mask.masked(value);
    const unmasked = this.mask.unmasked(this.mask.isEager() ? masked : value);
    const completed = this.mask.completed(value);
    const detail = { masked, unmasked, completed };
    value = masked;
    this.input.value = value;
    this.input.dataset.maskValue = value;
    this.input.dispatchEvent(new CustomEvent("mask", { detail }));
    this.input.dispatchEvent(new CustomEvent("input", { detail }));
  }
}
const masks = /* @__PURE__ */ new WeakMap();
const checkValue = (input) => {
  setTimeout(() => {
    if (masks.get(input)?.needUpdateValue() === true) {
      input.dispatchEvent(new CustomEvent("input"));
    }
  });
};
export const vMask = {
  mounted(el, binding) {
    const input = el instanceof HTMLInputElement ? el : el.querySelector("input");
    if (input == null || input?.type === "file") {
      return;
    }
    const opts = !binding.value || typeof binding.value === "string" ? { format: binding.value } : binding.value;
    if (!opts.format || !opts.format.length) {
      return;
    }
    checkValue(input);
    const existed = masks.get(input);
    if (existed != null) {
      if (!existed.needUpdateOptions(opts)) {
        return;
      }
      existed.destroy();
    }
    masks.set(input, new MaskInput(input, opts));
  },
  beforeUnmount(el) {
    const input = el instanceof HTMLInputElement ? el : el.querySelector("input");
    if (input == null || input?.type === "file") {
      return;
    }
    const existed = masks.get(input);
    if (existed != null) {
      existed.destroy();
    }
  }
};
